import React from "react";
import { useTranslation } from "react-i18next";
import { Identifiable, Group as G, project } from "@planzone/dto";

import {
  useProjectResourcesRetrieval,
  useRetrieveCurrentPlanzone,
  useRetrieveResourceGroups,
} from "@planzone/view-models";

import { useWorkspaceContext } from "@scenes/workspaceContext";
import { GroupIcon } from "@common/assets/groupIcon";
import { Loader } from "../loader";
import { Section } from "../section";
import { ScrollPane } from "../scrollPane";
import { filterSearch } from "../utils";
import { Props } from "./types";
import { Group } from "./group";
import styles from "./styles.scss";

function ResourceGroups({ search, filter, setFilter, isOpen }: Props) {
  const { t } = useTranslation();
  const { planzone } = useRetrieveCurrentPlanzone();
  const ctx = useWorkspaceContext();
  const isInProject = ctx.type === "project";
  const {
    isRequestPending: isPlanzoneRequestPending,
    isRequestSuccess: isPlanzoneRequestSuccess,
    isRequestFailure: isPlanzoneRequestFailure,
    groups,
    retrieveResourceGroups: retrievePlanzoneResourceGroups,
  } = useRetrieveResourceGroups(planzone.uuid);
  const {
    isRequestPending: isProjectRequestPending,
    isRequestSuccess: isProjectRequestSuccess,
    isRequestFailure: isProjectRequestFailure,
    groups: projectGroups,
    retrieveResourceGroups: retrieveProjectResourceGroups,
  } = useRetrieveResourceGroups(isInProject ? ctx.project.uuid : project.uuid);
  const { projectResources } = useProjectResourcesRetrieval(
    isInProject ? ctx.project : project,
  );
  const planzoneGroups = isInProject
    ? groups.reduce<Identifiable<G>[]>((filteredGroups, group) => {
        const filteredGroup = {
          ...group,
          resources: group.resources.filter(
            ({ uuid }) =>
              !!projectResources.find(({ resource }) => resource.uuid === uuid),
          ),
        };

        return filteredGroup.resources.length > 0
          ? [...filteredGroups, filteredGroup]
          : filteredGroups;
      }, [])
    : groups;
  const selectGroups = (groups: Identifiable<G>[]) =>
    setFilter(filter ? [...filter, ...groups] : groups);
  const unSelectGroups = (groups: Identifiable<G>[]) =>
    filter &&
    setFilter(
      filter.filter((group) => !groups.find(({ uuid }) => group.uuid === uuid)),
    );
  const isSelected = (group: Identifiable<G>) =>
    !!filter && !!filter.find(({ uuid }) => group.uuid === uuid);

  React.useEffect(() => {
    !isPlanzoneRequestPending &&
      !isPlanzoneRequestSuccess &&
      !isPlanzoneRequestFailure &&
      retrievePlanzoneResourceGroups({
        namespace: planzone.uuid,
        resourceType: "planzone",
      });
  }, []);

  React.useEffect(() => {
    !isProjectRequestPending &&
      !isProjectRequestSuccess &&
      !isProjectRequestFailure &&
      retrieveProjectResourceGroups({
        namespace: isInProject ? ctx.project.uuid : project.uuid,
        resourceType: "project",
      });
  }, []);

  return (
    <Section
      header={
        <div className={styles.header}>
          <div className={styles.iconBox}>
            <GroupIcon className={styles.icon} />
          </div>
          <p className={styles.title}>{t("common.filterMenu.groups.title")}</p>
        </div>
      }
      list={
        <ScrollPane>
          <>
            {planzoneGroups.length > 0 && (
              <div className={styles.list}>
                {!isPlanzoneRequestPending ? (
                  [...filterSearch(planzoneGroups, search)]
                    .sort((a, b) => a.name.localeCompare(b.name))
                    .map((group) => (
                      <Group
                        key={group.uuid}
                        group={group}
                        isSelected={isSelected(group)}
                        select={() => selectGroups([group])}
                        unSelect={() => unSelectGroups([group])}
                      />
                    ))
                ) : (
                  <Loader />
                )}
              </div>
            )}
          </>
          <>
            {isInProject && projectGroups.length > 0 && (
              <div className={styles.list}>
                {!isProjectRequestPending ? (
                  [...filterSearch(projectGroups, search)]
                    .sort((a, b) => a.name.localeCompare(b.name))
                    .map((group) => (
                      <Group
                        key={group.uuid}
                        group={group}
                        isSelected={isSelected(group)}
                        select={() => selectGroups([group])}
                        unSelect={() => unSelectGroups([group])}
                      />
                    ))
                ) : (
                  <Loader />
                )}
              </div>
            )}
          </>
        </ScrollPane>
      }
      isOpen={isOpen}
    />
  );
}

export { ResourceGroups };
